import type { Schema } from "./api";

/** Which engine runs an analysis and what it covers, as data the launch window reads. The server
 * decides admission; these rules only say it in the estimator's words. No runtime imports, so the
 * module runs under the plain Node test runner. */
export type Provider = Schema["AnalysisStart"]["provider"];
export type Mode = Schema["AnalysisStart"]["mode"];
export type Pair = [string, string];
type Job = Schema["JobOut"];
type Options = Schema["AnalysisOptionsOut"];
type Pin = Schema["AnalysisPin"];
type Connection = Schema["CodexConnection"];
type Section = Schema["ScopeSection"];
type ScopeDocument = Schema["ScopeDocument"];

export const providerLabel: Record<Provider, string> = { codex: "Codex · подписка ChatGPT", openai_api: "OpenAI API" };
/** How each engine is paid for, said once on its card. */
export const providerDescription: Record<Provider, string> = {
  codex: "Расходует лимиты подписки ChatGPT; долларовая оценка недоступна.",
  openai_api: "Оплата по тарифам API в пределах заданного предела расходов.",
};
export const NO_FALLBACK = "Автоматического перехода на OpenAI API нет.";

const clock = (value: string) => new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });

/** Analyses made before Codex carry no provider: they are the OpenAI API. */
export function jobProvider(job: Pick<Job, "provider" | "details">): Provider {
  return job.provider ?? (job.details.provider === "codex" ? "codex" : "openai_api");
}

/** The job that fixes this launch's engine and scope: a running analysis, else the stopped one of the mode. */
export function launchPin(options: Options | undefined, mode: Mode): Pin | undefined {
  const pins = options?.pins ?? [];
  return pins.find(pin => pin.status === "queued" || pin.status === "running") ?? pins.find(pin => pin.mode === mode);
}

export function launchProvider(options: Options | undefined, mode: Mode, chosen: Provider | null): Provider | null {
  return launchPin(options, mode)?.provider ?? chosen ?? options?.default_provider ?? null;
}

/** The request body. A Codex launch never names a dollar limit or an API tier; categories only when chosen. */
export function launchRequest(input: { provider: Provider; mode: Mode; budget?: string; tier?: "flex" | "default";
  categories?: Pair[] | null }): Schema["AnalysisStart"] {
  if (input.provider === "codex") {
    return { provider: "codex", mode: input.mode, ...(input.mode === "all" && input.categories ? { categories: input.categories } : {}) };
  }
  return { provider: "openai_api", mode: input.mode, budget_usd: input.budget ?? null, service_tier: input.tier ?? null };
}

export const pairKey = (pair: Pair) => `${pair[0]}: ${pair[1]}`;

/** The API states each category as a two-item list [section, category]. */
export const asPairs = (values: string[][]): Pair[] =>
  values.filter(value => value.length === 2).map(([section, category]) => [section, category]);

export function samePairs(left: Pair[], right: Pair[]) {
  const keys = new Set(left.map(pairKey));
  return keys.size === new Set(right.map(pairKey)).size && right.every(pair => keys.has(pairKey(pair)));
}

function ordered(keys: Set<string>, sections: Section[]): Pair[] {
  return sections.flatMap(section => section.categories.map(category => [section.section, category] as Pair))
    .filter(pair => keys.has(pairKey(pair)));
}

export function toggleCategory(pairs: Pair[], pair: Pair, on: boolean, sections: Section[]): Pair[] {
  const keys = new Set(pairs.map(pairKey));
  if (on) keys.add(pairKey(pair)); else keys.delete(pairKey(pair));
  return ordered(keys, sections);
}

export function toggleSection(pairs: Pair[], section: string, on: boolean, sections: Section[]): Pair[] {
  const keys = new Set(pairs.map(pairKey));
  for (const category of sections.find(row => row.section === section)?.categories ?? []) {
    if (on) keys.add(pairKey([section, category])); else keys.delete(pairKey([section, category]));
  }
  return ordered(keys, sections);
}

/** Each section with its share of categories and, always, the sections left out. */
export function scopeSummary(pairs: Pair[], sections: Section[]) {
  if (!pairs.length) return "Категории не выбраны.";
  const parts = sections.flatMap(({ section, categories }) => {
    const chosen = categories.filter(category => pairs.some(pair => pair[0] === section && pair[1] === category));
    if (!chosen.length) return [];
    return [chosen.length === categories.length ? `${section} — все категории (${categories.length})`
      : `${section} — ${chosen.length} из ${categories.length}: ${chosen.join(", ")}`];
  });
  const excluded = sections.filter(({ section }) => !pairs.some(pair => pair[0] === section)).map(row => row.section);
  return `${parts.join("; ")}.${excluded.length ? ` Не входит: ${excluded.join(", ")}.` : ""}`;
}

export function documentSummary(selected: string[] | null, documents: ScopeDocument[]) {
  const ready = documents.filter(document => document.status === "ready");
  if (!selected) return `Документы: все подготовленные документы редакции (${ready.length}).`;
  const chosen = ready.filter(document => selected.includes(document.id));
  return `Документы: ${chosen.length} из ${ready.length} — ${chosen.map(document => document.path).join(", ")}.`;
}

export type ConnectionNotice = { role: "status" | "alert"; title: string; detail: string; ready: boolean };

/** The single status of the connection: its state, who acts and that nothing switches to the API. The
 * probe's own text is for the administrator (it may name server settings) and is never shown here. */
export function connectionNotice(connection: Connection | undefined, time: (value: string) => string = clock): ConnectionNotice {
  if (!connection) return { role: "status", title: "Проверяем подключение Codex…", detail: "", ready: false };
  switch (connection.state) {
    case "ready":
      return { role: "status", ready: true, title: "Подключение Codex готово.",
        detail: [connection.model, connection.reasoning_effort && `reasoning ${connection.reasoning_effort}`,
          connection.checked_at && `проверено ${time(connection.checked_at)}`].filter(Boolean).join(" · ") };
    case "auth_required":
      // Also a model or reasoning level this ChatGPT account cannot use.
      return { role: "alert", ready: false, title: "Codex недоступен.",
        detail: `Администратор сервиса должен проверить вход в ChatGPT и доступ к модели. ${NO_FALLBACK}` };
    case "disabled":
      return { role: "alert", ready: false, title: "Анализ через Codex не включён на сервере.",
        detail: `Включить его может администратор сервиса. ${NO_FALLBACK}` };
    default:
      return { role: "status", ready: false, title: "Подключение Codex не проверено.",
        detail: `Администратор сервиса должен проверить подключение. ${NO_FALLBACK}` };
  }
}

/** What the paused or running job has already saved; its categories and batches are kept. */
export function savedProgress(job: Job | undefined) {
  if (!job) return null;
  const details = job.details as { categories?: Pair[]; coverage?: Record<string, { status?: string }>;
    partial_category?: { discipline?: string; category?: string; works?: number } };
  const categories = Array.isArray(details.categories) ? details.categories : [];
  const coverage = details.coverage ?? {};
  const parts = [];
  if (categories.length) parts.push(`готово категорий ${categories.filter(pair => coverage[pairKey(pair)]?.status === "done").length} из ${categories.length}`);
  const partial = details.partial_category;
  if (partial?.discipline && partial.category && coverage[`${partial.discipline}: ${partial.category}`]?.status !== "done") {
    parts.push(`«${partial.discipline}: ${partial.category}» — сохранено работ: ${partial.works ?? 0}`);
  }
  parts.push(`выполнено ${job.progress}%`);
  return parts.join(" · ");
}

export function codexLaunchLabel(pin: Pin | undefined, view: { mode: Mode; processing: boolean; submitting: boolean }) {
  if (view.submitting) return "Отправляем…";
  if (view.processing || (pin && (pin.status === "queued" || pin.status === "running"))) return "Обработка идёт";
  if (pin) return view.mode === "norms" ? "Продолжить подбор норм" : "Продолжить анализ";
  return view.mode === "norms" ? "Подобрать нормы" : "Начать анализ";
}
